import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserDto } from '@bstm/shared';
import { api, getToken, setToken } from '../api/client';

interface AuthState {
  user: UserDto | null;
  loading: boolean;
  loginWithToken: (token: string) => void;
  logout: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [tokenPresent, setTokenPresent] = useState(() => Boolean(getToken()));

  const meQuery = useQuery<{ user: UserDto }>({
    queryKey: ['me'],
    queryFn: () => api('/me'),
    enabled: tokenPresent,
    refetchInterval: (q) => {
      const status = q.state.data?.user?.status;
      return status === 'PENDING_APPROVAL' ? 15_000 : false;
    },
    retry: false,
  });

  useEffect(() => {
    if (meQuery.error) {
      setToken(null);
      setTokenPresent(false);
    }
  }, [meQuery.error]);

  const value: AuthState = {
    user: meQuery.data?.user ?? null,
    loading: tokenPresent && meQuery.isLoading,
    loginWithToken: (token) => {
      setToken(token);
      setTokenPresent(true);
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    logout: () => {
      setToken(null);
      setTokenPresent(false);
      qc.clear();
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
